import { Box, Button, Collapse, IconButton, Modal, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ExpandLess, ExpandMore, Folder } from "@mui/icons-material";
import { t } from "i18next";
import { IProjectData } from "@types";
import { toast } from "react-toastify";
import { CloseIcon, editFolderContents, getFolders } from "common-lib";

const FolderModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  width: "35%",
  height: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: 2,
  backgroundColor: "var(--dk-surface, #fff)",
};

interface FolderModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  checkedProjects: number[];
  checkedFolders: number[];
  projectsData: IProjectData[];
  setProjectsData: (projectsData: IProjectData[]) => void;
  folderList: FolderItem[];
  setFolderList: Dispatch<SetStateAction<FolderItem[]>>;
}

export default function FolderModal({
  open,
  setOpen,
  checkedProjects,
  checkedFolders,
  projectsData,
  setProjectsData,
  folderList,
  setFolderList,
}: FolderModalProps) {
  const [treeData, setTreeData] = useState<FolderItem[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<FolderItem | null>(null);

  const fetchFolders = async () => {
    try {
      const response = await getFolders();
      setTreeData(response.folders);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchFolders();
  }, []);

  const handleConfirm = async () => {
    //   원본폴더로 이동 클릭 시 이를 방지하는 코드 필요
    try {
      if (selectedFolder) {
        await editFolderContents(selectedFolder.id, checkedFolders, checkedProjects);
      } else {
        await editFolderContents(null, checkedFolders, checkedProjects);
      }

      setProjectsData(projectsData.filter((project) => !checkedProjects.includes(project.id)));
      setFolderList(folderList.filter((folder) => !checkedFolders.includes(folder.id)));

      setOpen(false);
      toast.success("해당 폴더로 이동을 완료했습니다.");
    } catch (error: any) {
      console.error("폴더 또는 프로젝트 이동 중 오류 발생:", error);
      toast.error(error.message || "이동 중 오류가 발생했습니다. 다시 시도해주세요.");
    }
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <Box
        sx={{
          ...FolderModalStyle,
          background: "var(--dk-surface, #fff)",
          borderRadius: "14px",
          border: "1px solid var(--dk-border, #e5e7eb)",
          boxShadow: "0 16px 48px rgba(0,0,0,0.10)",
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: "20px",
            py: "14px",
            borderBottom: "1px solid var(--dk-border, #f3f4f6)",
            background: "var(--dk-surface-2, #fafafa)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "7px",
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Folder sx={{ fontSize: 15, color: "#f97316" }} />
            </Box>
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "var(--dk-text, #111827)" }}>위치 선택</Typography>
          </Box>
          <IconButton
            onClick={() => setOpen(false)}
            size="small"
            sx={{
              width: 26,
              height: 26,
              borderRadius: "6px",
              color: "var(--dk-text-3, #9ca3af)",
              "&:hover": { background: "var(--dk-surface-2, #f3f4f6)", color: "var(--dk-text-2, #374151)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: "hidden", px: "16px", py: "12px" }}>
          <Box
            onClick={() => setSelectedFolder(null)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              px: "10px",
              py: "7px",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all .12s",
              background: !selectedFolder ? "#eff6ff" : "transparent",
              border: !selectedFolder ? "1px solid #bfdbfe" : "1px solid transparent",
              mb: "4px",
            }}
          >
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: "5px",
                background: !selectedFolder ? "#dbeafe" : "#f3f4f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Folder sx={{ fontSize: 13, color: !selectedFolder ? "#3b82f6" : "#9ca3af" }} />
            </Box>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: !selectedFolder ? 600 : 400,
                color: !selectedFolder ? "#3b82f6" : "#374151",
              }}
            >
              root
            </Typography>
            {!selectedFolder && (
              <Box
                sx={{
                  ml: "auto",
                  fontSize: "10px",
                  fontWeight: 600,
                  px: "6px",
                  py: "1px",
                  borderRadius: "20px",
                  background: "#dbeafe",
                  color: "#3b82f6",
                }}
              >
                선택됨
              </Box>
            )}
          </Box>
          <FolderTreeView folders={treeData} selectedFolder={selectedFolder} setSelectedFolder={setSelectedFolder} />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "6px",
            px: "20px",
            py: "12px",
            borderTop: "1px solid var(--dk-border, #f3f4f6)",
            background: "var(--dk-surface-2, #fafafa)",
          }}
        >
          <button
            onClick={() => setOpen(false)}
            style={{
              padding: "6px 14px",
              borderRadius: "8px",
              border: "1px solid var(--dk-border, #e5e7eb)",
              background: "var(--dk-surface, #fff)",
              fontSize: "12px",
              fontWeight: 500,
              color: "var(--dk-text-2, #6b7280)",
              cursor: "pointer",
              transition: "all .15s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = "#d1d5db";
              (e.currentTarget as HTMLElement).style.color = "#374151";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = "#e5e7eb";
              (e.currentTarget as HTMLElement).style.color = "#6b7280";
            }}
          >
            {t("Cancel")}
          </button>
          <button
            onClick={handleConfirm}
            style={{
              padding: "6px 16px",
              borderRadius: "8px",
              border: "1px solid var(--dk-info-border, #bfdbfe)",
              background: "var(--dk-info-bg, #eff6ff)",
              fontSize: "12px",
              fontWeight: 600,
              color: "#3b82f6",
              cursor: "pointer",
              transition: "all .15s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#3b82f6";
              (e.currentTarget as HTMLElement).style.color = "#fff";
              (e.currentTarget as HTMLElement).style.borderColor = "#3b82f6";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#eff6ff";
              (e.currentTarget as HTMLElement).style.color = "#3b82f6";
              (e.currentTarget as HTMLElement).style.borderColor = "#bfdbfe";
            }}
          >
            {t("Confirm")}
          </button>
        </Box>
      </Box>
    </Modal>
  );
}

type FolderTree = FolderItem & { children: FolderTree[] };

// 폴더 데이터를 트리 구조로 변환
const buildFolderTree = (folders: FolderItem[]): FolderTree[] => {
  const map = new Map<number, FolderTree>();
  const roots: FolderTree[] = [];

  folders.forEach((folder) => {
    map.set(folder.id, { ...folder, children: [] });
  });

  folders.forEach((folder) => {
    if (folder.parent === null) {
      roots.push(map.get(folder.id)!);
    } else {
      const parent = map.get(folder.parent);
      if (parent) parent.children.push(map.get(folder.id)!);
    }
  });

  return roots;
};

interface TreeItemProps {
  folder: FolderTree;
  selectedFolder: FolderItem | null;
  setSelectedFolder: (folder: FolderItem | null) => void;
}

const TreeItem = ({ folder, selectedFolder, setSelectedFolder }: TreeItemProps) => {
  const [open, setOpen] = useState(false);
  const isSelected = selectedFolder?.id === folder.id;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(!open);
  };

  const handleSelectFolder = () => {
    setSelectedFolder(folder);
  };

  return (
    <Box sx={{ pl: "12px" }}>
      <Box
        onClick={handleSelectFolder}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          px: "8px",
          py: "5px",
          borderRadius: "7px",
          cursor: "pointer",
          transition: "all .12s",
          background: isSelected ? "#eff6ff" : "transparent",
          border: isSelected ? "1px solid #bfdbfe" : "1px solid transparent",
          mb: "2px",
          "&:hover": {
            background: isSelected ? "#eff6ff" : "#f9fafb",
          },
        }}
      >
        <IconButton
          size="small"
          onClick={handleToggle}
          sx={{
            width: 18,
            height: 18,
            p: 0,
            color: open ? "#6b7280" : "#d1d5db",
            flexShrink: 0,
            "&:hover": { background: "transparent", color: "var(--dk-text-2, #374151)" },
          }}
        >
          {folder.children?.length > 0 ? (
            open ? (
              <ExpandLess sx={{ fontSize: 15 }} />
            ) : (
              <ExpandMore sx={{ fontSize: 15 }} />
            )
          ) : (
            <Box sx={{ width: 15 }} />
          )}
        </IconButton>
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: "4px",
            background: isSelected ? "#dbeafe" : "#f3f4f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Folder sx={{ fontSize: 13, color: isSelected ? "#3b82f6" : "#9ca3af" }} />
        </Box>
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: isSelected ? 600 : 400,
            color: isSelected ? "#3b82f6" : "#374151",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {folder.name}
        </Typography>
        {isSelected && (
          <Box
            sx={{
              fontSize: "10px",
              fontWeight: 600,
              px: "6px",
              py: "1px",
              borderRadius: "20px",
              background: "#dbeafe",
              color: "#3b82f6",
              flexShrink: 0,
            }}
          >
            선택됨
          </Box>
        )}
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        {folder.children?.map((child) => (
          <TreeItem
            key={child.id}
            folder={child}
            selectedFolder={selectedFolder}
            setSelectedFolder={setSelectedFolder}
          />
        ))}
      </Collapse>
    </Box>
  );
};

interface FolderTreeView {
  folders: FolderItem[];
  selectedFolder: FolderItem | null;
  setSelectedFolder: (folder: FolderItem | null) => void;
}

// 전체 트리 컴포넌트
const FolderTreeView = ({ folders, selectedFolder, setSelectedFolder }: FolderTreeView) => {
  const treeData = buildFolderTree(folders);

  return (
    <Box sx={{ height: "100%" }}>
      {treeData.map((folder) => (
        <TreeItem
          key={folder.id}
          folder={folder}
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
        />
      ))}
    </Box>
  );
};
